import { CalendarMonthExistingGestures } from "@tento-ui/components/calendar-month/calendar-month-existing-gestures";
import { CalendarMonthNewGestures } from "@tento-ui/components/calendar-month/calendar-month-new-gestures";
import type { CalendarMonthGesture } from "@tento-ui/components/calendar-month/calendar-month-gesture";
import type { CalendarMonthSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-month-subcolumn.ts";

import type { CalendarMonthActions } from "./calendar-month-actions.ts";

/**
 * Register the 8 calendar-month event gestures (3 existing-event + 5
 * new-event) on the host's gesture manager. Mirrors the inline wiring
 * that lived in `CalendarMonthHost`'s constructor before the Phase-3
 * decouple.
 */
export function registerCalendarMonthGestures(
  actions: CalendarMonthActions,
  host: CalendarMonthSubcolumn.Host,
): void {
  // Shared actions for the new-event gestures. Identical to the
  // pre-decouple `CalendarMonthHost.createActions` closure.
  const createActions: CalendarMonthNewGestures.Actions = {
    onCreateStart: (date) => actions.onCreateStart(date),
    onCreateDrag: (dr) => actions.onCreateDrag(dr),
    onCreateDragEnd: (dr) => actions.onCreateDragEnd(dr),
    getPendingSpan: () => actions.buildPendingSpan(),
    isCreateInProgress: () =>
      actions.hasPendingEvent() || actions.isPopoverActive(),
    hideDetails: () => actions.hidePopoverTemporarily(),
    reshowDetails: (dateRange) => {
      actions.setPendingEventForReshow(dateRange);
      actions.reshowPopover();
    },
    shouldBlockForCrossSystemDismissal: (isPointerDown) =>
      host.shouldBlockForCrossSystemDismissal(isPointerDown),
    isContextMenuActive: () => actions.isContextMenuActive(),
    dismissContextMenu: () => actions.dismissContextMenu(),
  };

  host.gestureManager
    .addGesture(
      new CalendarMonthExistingGestures.ContextMenu((e) =>
        actions.onSpanContextMenu(e),
      ),
    )
    // ResizeSpan must precede MoveSpan: the manager picks the first
    // gesture whose `canHandle` matches (`calendar-month-gesture.ts`).
    // MoveSpan accepts any pointer-down on a span, so registering it
    // first would steal handle-edge clicks before ResizeSpan (which
    // gates on `pointerDown.resizeEdge`) gets a look. Mirrors the
    // storybook order in `calendar-month.interactive.example.ts`.
    .addGesture(
      new CalendarMonthExistingGestures.ResizeSpan(
        (e) => actions.onSpanResized(e),
        // Atomic resize-promotion: same key-stable shape as `MoveSpan`,
        // but the gesture provides an absolute `newRange` directly.
        (span, newRange) =>
          host.$calendar.setResizeOverride(span?.key ?? null, newRange),
      ),
    )
    .addGesture(
      new CalendarMonthExistingGestures.MoveSpan(
        (e) => actions.onSpanMoved(e),
        // Atomic drag-promotion: the dragged span IS the real span in
        // a "pending" visual state. The widget shifts its effective
        // range by `offsetDays` in place, keyed by `span.key` — so on
        // drop the same DOM is reused (no move-ghost exit + real-span
        // create seam).
        (span, offsetDays) =>
          host.$calendar.setDragOffset(span?.key ?? null, offsetDays),
        (e) => actions.onSpanClicked(e),
      ),
    )
    .addGesture(
      new CalendarMonthExistingGestures.MoveEntryRow<CalendarMonthGesture.Info>(
        (e) => actions.onEntryRowMoved(e),
        (span, offsetDays) => host.$calendar.setGhostSpan(span, offsetDays),
        (e) => actions.onEntryRowClicked(e),
      ),
    )
    .addGesture(
      new CalendarMonthNewGestures.DismissCreate(createActions, () =>
        actions.cancelPendingEvent(),
      ),
    )
    .addGesture(new CalendarMonthNewGestures.Resize(createActions))
    .addGesture(new CalendarMonthNewGestures.Move(createActions))
    .addGesture(new CalendarMonthNewGestures.DragToCreate(createActions))
    .addGesture(new CalendarMonthNewGestures.ClickToCreate(createActions));
}
